import { memo, useEffect, useRef, useState } from 'react';
import { uuid } from '@betty/toolkit/utils';
import { getModals } from '../utils/registry';
import { Box, Modal as ModalContainer } from '@betty/moonlight';
import {
  ModalConfig,
  ModalsManagerExposedProps,
  ModalType,
} from './ModalsManager.types';
import { useDisableScroll } from '@betty/toolkit/hooks';
import useLocation from '../hooks/useLocation/useLocation';
import {
  appAuthenticated,
  initiateLogin,
  selectAuthenticationStatus,
  startListening,
  useDispatch,
  useSelector,
} from '@betty/toolkit/state';
import { NavigationRefComponent } from '../navigationRef';
import ThemedNavigationContainer from '../ThemedNavigationContainer';
import { NavigationIndependentTree } from '@react-navigation/native';
import { ErrorBoundaryProvider } from '../hoc';
import { useTranslation } from '@betty/i18n';

export let Modal: ModalsManagerExposedProps = {
  open: () => {},
  close: () => Promise.resolve(),
  getCurrentModal: () => undefined,
};

function ModalsManager() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [modals, setModals] = useState<ModalConfig[]>([]);
  const { pathname } = useLocation();
  const pathnameRef = useRef(pathname);
  const modalsRef = useRef(modals);
  const authStatus = useSelector(selectAuthenticationStatus);

  modalsRef.current = modals;

  useEffect(() => {
    if (pathname !== pathnameRef.current) {
      pathnameRef.current = pathname;
      modals.forEach((modal) => modal.close());
    }
  }, [modals, pathname]);

  useEffect(() => {
    const handleClose = (id?: string) =>
      new Promise<void>((resolve) => {
        setModals((modals) => {
          const draft = [...modals];
          const targetId = id ?? modals[modals.length - 1]?.id;
          const index = draft.findIndex((modal) => modal.id === targetId);

          if (index === -1) {
            resolve();

            return modals;
          }

          draft[index].closing = true;
          draft[index].onClosed = () => {
            setModals((modals) =>
              modals.filter((modal) => modal.id !== targetId),
            );

            const userOnClosed = draft[index].onClosedProp;
            if (typeof userOnClosed === 'function') {
              userOnClosed();
            }
            resolve();
          };

          return draft;
        });
      });

    Modal = {
      open: (config) => {
        const {
          namespace,
          options,
          style,
          data,
          canClose = true,
          isProtected,
        } = config;
        const { queued, ...restOptions } = options || {};
        const type = config.type as ModalType<
          typeof namespace,
          typeof config.type
        >;
        const id = uuid();

        const insertModal = () => {
          setModals((modals) => {
            const modal = {
              id,
              data,
              type,
              style,
              canClose,
              Component: getModals()[namespace][type],
              close: () => handleClose(id),
              onClosedProp: restOptions.onClosed,
              ...restOptions,
            };

            return queued ? [modal, ...modals] : [...modals, modal];
          });
        };

        if (isProtected && authStatus !== 'authenticated') {
          dispatch(initiateLogin());

          const unsubscribe = startListening({
            actionCreator: appAuthenticated,
            effect: () => {
              insertModal();

              unsubscribe();
            },
          });
        } else {
          insertModal();
        }
      },
      close: () => handleClose(),
      getCurrentModal: () =>
        modalsRef.current[modalsRef.current.length - 1]?.type,
    };
  }, [authStatus, dispatch]);

  useDisableScroll(modals.length > 0);

  return (
    <Box absoluteFill centerContent>
      <title>{t('seoTitle')}</title>
      <meta property="og:title" content={t('seoTitle')} />
      <meta name="description" content={t('seoDescription')} />
      <meta property="og:description" content={t('seoDescription')} />
      {modals.map(({ id, data, Component, close, ...rest }) => (
        <ModalContainer key={id} id={id} close={close} {...rest}>
          <ErrorBoundaryProvider>
            <NavigationIndependentTree>
              <ThemedNavigationContainer>
                <Component {...data} close={close} />
                <NavigationRefComponent />
              </ThemedNavigationContainer>
            </NavigationIndependentTree>
          </ErrorBoundaryProvider>
        </ModalContainer>
      ))}
    </Box>
  );
}

export default memo(ModalsManager);
